import { ZERO_INCHES } from "common/constants/scaleFactors";
import type { FontId } from "common/constants/textFont";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { LRUStringCache } from "common/utils/lruStringCache";
import { numDiv, numMax, numMin, numMul } from "common/utils/nominalNumbers";
import stableHash from "common/utils/stableHash";
import { create, type Font } from "fontkit";
import { registerFontSetCallback } from "pdf/fontCallbacks";
import { getFont } from "pdf/fonts";
import { roundPDFWrapComparisonInches } from "./pdfNormalization";
import type { TextSize, TextSizeInput } from "./TextMetricsProvider";

// Register to receive font set notifications.
registerFontSetCallback((fontId: string) => {
  PDFTextMetricsProvider.onFontSet(fontId);
});

/**
 * Uses fontkit directly to measure text size. It's important to call loadFonts before
 * using this class as it can only measure fonts loaded with that API.
 */
export class PDFTextMetricsProvider {
  private static _instance: PDFTextMetricsProvider;
  private static readonly fonts = new Map<string, Font>();

  /**
   * Text measurement can be quite expensive, so we've provided a cache
   */
  private static cache = new LRUStringCache<TextSize>(1000 /* cache size */);
  private static capHeightCache = new LRUStringCache<number>(20 /* cache size */);

  private constructor() {}

  static get instance() {
    return this._instance || (this._instance = new PDFTextMetricsProvider());
  }

  /**
   * Clear caches when a font is loaded or replaced.
   */
  static onFontSet(fontName: string) {
    PDFTextMetricsProvider.fonts.delete(fontName);
    PDFTextMetricsProvider.cache.clear();
    PDFTextMetricsProvider.capHeightCache.delete(stableHash(fontName));
  }

  /**
   * heightOfString is provided for legacy purposes. Note it uses the default plan set font.
   */
  heightOfString(input: TextSizeInput): SheetInches {
    return this.measureText(input).height;
  }

  /**
   * widthOfString is provided for legacy purposes. Note it uses the default plan set font.
   */
  widthOfString(input: TextSizeInput): SheetInches {
    return this.measureText(input).width;
  }

  private measureTextInternal(textInput: TextSizeInput): TextSize {
    // We narrow to the fields that actually effect the size of the text
    const text = {
      boxWidth: textInput.boxWidth,
      text: textInput.text.replace(/\t/g, "    "),
      font: textInput.font,
      fontSize: textInput.fontSize,
    } as const;

    const hash = stableHash(text);
    const e = PDFTextMetricsProvider.cache.get(hash);
    if (e) {
      // make a defensive copy to prevent callers from mutating the cache
      return { ...e };
    }

    assertIsNotUndefined(text.fontSize);
    assertIsNotUndefined(text.font);

    const fontSizeInches = this.scaleFontSizeToCaps(text.font, text.fontSize);
    const singleLineWidth = this.measureSingleLineWidth(text.text, text.font, fontSizeInches);
    const width = text.boxWidth ? numMin(singleLineWidth, text.boxWidth) : singleLineWidth;
    const height = text.text.length === 0 ? ZERO_INCHES : this.lineHeight(text.fontSize, text.font);

    const size = { width, height };

    // Note: We use textNoPos to build the cache key
    PDFTextMetricsProvider.cache.add(hash, size);

    return { ...size };
  }

  private measureSingleLineWidth(text: string, fontId: FontId, fontSizeInches: SheetInches): SheetInches {
    if (text.length === 0) {
      return ZERO_INCHES;
    }

    const font = PDFTextMetricsProvider.getParsedFont(fontId);
    const glyphRun = font.layout(text);
    return numDiv(numMul(fontSizeInches, glyphRun.advanceWidth), font.unitsPerEm);
  }

  measureText(input: TextSizeInput): TextSize {
    if (input.boxWidth === undefined && !input.text.includes("\n")) {
      return this.measureTextInternal(input);
    }

    const lineHeight = this.lineHeight(input.fontSize, input.font);

    const lines = wrapText(input);
    const width = lines.reduce(
      (previous, text) => numMax(previous, this.widthOfString({ ...input, text, boxWidth: undefined })),
      ZERO_INCHES,
    );

    return { width, height: numMul(lineHeight, lines.length) };
  }

  lineHeight(fontSize: SheetInches, fontId: FontId): SheetInches {
    const font = PDFTextMetricsProvider.getParsedFont(fontId);
    const scaledFontSize = this.scaleFontSizeToCaps(fontId, fontSize);
    return numMul(scaledFontSize, (font.ascent + font.lineGap - font.descent) / font.unitsPerEm);
  }

  xWidth(fontSize: SheetInches, font: FontId): SheetInches {
    return this.measureTextInternal({ text: "X", fontSize, font }).width;
  }

  capHeightScaleFactor(fontId: FontId): number {
    const hash = stableHash(fontId);
    const e = PDFTextMetricsProvider.capHeightCache.get(hash);
    if (e) {
      return e;
    }

    const font = PDFTextMetricsProvider.getParsedFont(fontId);

    const capHeight = capHeightForFont(font);
    const scaleFactor = font.unitsPerEm / capHeight;

    // Uncomment for logging.
    // Arial Narrow = 1.3969986357435198
    // Circular = 1.4104372355430184
    // console.log("Caps height scale factor for " + fontName + " is " + scaleFactor);

    PDFTextMetricsProvider.capHeightCache.add(hash, scaleFactor);

    return scaleFactor;
  }

  /**
   * Returns a scaled font size that fits the cap height into the line height.
   * @param fontName Name of the font face
   * @param fontSize Size of the font in inches
   */
  scaleFontSizeToCaps(font: FontId, fontSize: SheetInches): SheetInches {
    const scaleFactor = this.capHeightScaleFactor(font);
    if (scaleFactor > 0) {
      return numMul(fontSize, scaleFactor);
    }
    return fontSize;
  }

  private static getParsedFont(fontId: FontId): Font {
    const existingFont = PDFTextMetricsProvider.fonts.get(fontId);
    if (existingFont) {
      return existingFont;
    }

    const fontBuffer = getFont(fontId);
    assertIsNotUndefined(fontBuffer);

    const parsedFont = create(Buffer.from(fontBuffer));
    assertIsFont(parsedFont);
    PDFTextMetricsProvider.fonts.set(fontId, parsedFont);
    return parsedFont;
  }
}

function assertIsFont(font: Font | { fonts: Font[] }): asserts font is Font {
  if (!("postscriptName" in font)) {
    throw new Error("Expected a single font, but got a font collection");
  }
}

function capHeightForFont(font: Font): number {
  // If the font has an embedded metric for cap height, just return that.
  if (font.capHeight) {
    return font.capHeight;
  }

  // No cap height metric, so we can use the glyph bounding box.
  // Although some caps are slightly taller or smaller, let's use "X" as a baseline.
  const glyphX = font.glyphsForString("X")[0];
  return glyphX.bbox.height;
}

export function wrapText(input: TextSizeInput): string[] {
  const wrapLines: string[] = [];
  const textLines = input.text.split("\n");

  for (const text of textLines) {
    if (input.boxWidth) {
      wrapLines.push(...chunkString({ ...input, text, boxWidth: input.boxWidth }));
    } else {
      wrapLines.push(text);
    }
  }

  return wrapLines;
}

// The code below is adapted from troika-three-text (Typesetter.js):

// This regex (instead of /\s/) allows us to select all whitespace EXCEPT for non-breaking white spaces
const lineBreakingWhiteSpace = `[^\\S\\u00A0]`;

// Incomplete set of characters that allow line breaking after them
// In the future we may consider a full Unicode line breaking algorithm impl: https://www.unicode.org/reports/tr14
const BREAK_AFTER_CHARS = new RegExp(
  `${lineBreakingWhiteSpace}|[\\-\\u007C\\u00AD\\u2010\\u2012-\\u2014\\u2027\\u2056\\u2E17\\u2E40]`,
);

function chunkString(input: Required<TextSizeInput>): string[] {
  const result: string[] = [];

  let currString = "";
  const isWhitespace = new RegExp(lineBreakingWhiteSpace);

  if (input.text.length * input.fontSize * 0.9 < input.boxWidth) {
    // Try to early out as an optimization, the whole string very likely fits on one line based on a conservative size estimate.
    const text = input.text.trimEnd();
    const testWidth = PDFTextMetricsProvider.instance.widthOfString({
      ...input,
      text,
      boxWidth: undefined,
    });
    if (testWidth <= input.boxWidth) {
      return [text];
    }
  }

  for (const char of input.text) {
    const newWidth = PDFTextMetricsProvider.instance.widthOfString({
      ...input,
      text: currString + char,
      boxWidth: undefined,
    });

    const roundedNewWidth = roundWrapComparisonWidth(newWidth);
    const roundedBoxWidth = roundWrapComparisonWidth(input.boxWidth);

    if (roundedNewWidth > roundedBoxWidth && !isWhitespace.test(char)) {
      // If it's the first char after a whitespace, start a new line
      if (currString !== "" && BREAK_AFTER_CHARS.test(currString.slice(-1))) {
        result.push(currString.trimEnd());
        currString = "";
      } else {
        // Back up through the whole line looking for the last break character to wrap at, so we
        // never split a word when there's an earlier break point it could fit at instead.
        let breakIndex = -1;
        for (let i = currString.length; i-- > 0; ) {
          if (BREAK_AFTER_CHARS.test(currString[i])) {
            breakIndex = i;
            break;
          }
        }
        if (breakIndex >= 0) {
          // Found a soft break point; move everything after it to the next line. trimEnd() drops a
          // trailing space so it isn't counted in the line's width, but keeps a trailing hyphen/dash.
          result.push(currString.slice(0, breakIndex + 1).trimEnd());
          currString = currString.slice(breakIndex + 1);
        } else {
          // No soft break point anywhere; the line is a single long token, so make a hard break
          result.push(currString.trimEnd());
          currString = "";
        }
      }
    }

    currString += char;
  }

  result.push(currString.trimEnd());

  return result;
}

function roundWrapComparisonWidth(width: SheetInches): SheetInches {
  return roundPDFWrapComparisonInches(width);
}
